<template>
  <button
    :type="type"
    class="button is-rounded is-large"
    :class="[
      `is-${bgColor}`,
      themedClass,
      {
        'is-loading': loading,
        'is-fullwidth-on-mobile': text,
        'min-width': text && minWidth,
        [`variant-${variantOverride}`]: variantOverride,
      },
    ]"
    :disabled="disabled"
    @click="onClick($event)"
  >
    <div v-if="icon" style="display: flex;" :class="{ 'add-right-padding': text }">
      <LsIcon :type="icon" :color="normalizedTextColor" />
    </div>
    <span
      v-if="text"
      class="is-size-5 has-text-weight-bold"
      :class="[
        {
          ['is-uppercase']: isUppercase,
        },
      ]"
      >{{ text }}</span
    >
  </button>
</template>

<script>
import _ from 'lodash';
import { lsPropTypeHelpers, lsTrackingService } from 'ls-vue-common';
import { lsColors } from '@/constants';
import LsIcon from '@/components/common/LsIcon/LsIcon.vue';
import { LsIconTypes } from '@/components/common/LsIcon/LsIcon.constants';
import { themedComponentMixin } from '@/mixins';
import VueTypes from 'vue-types';

export default {
  name: 'LsButton',
  components: {
    LsIcon,
  },
  mixins: [themedComponentMixin],
  props: {
    trackId: VueTypes.custom(lsPropTypeHelpers.trackID).isRequired,
    text: VueTypes.string,
    icon: VueTypes.oneOf(LsIconTypes),
    type: VueTypes.oneOf(['button', 'submit']).def('button'),
    bgColor: VueTypes.oneOf(_.keys(lsColors)).def('tertiary'),
    textColor: VueTypes.oneOf(_.keys(lsColors)).def('white'),
    loading: VueTypes.bool.def(false),
    disabled: VueTypes.bool.def(false),
    minWidth: VueTypes.bool.def(true),
    // pre-defined variantOverride options have a higher
    // specificity than any other bg color or hover colors that
    // might also be passed in. See variant-primary-outlined class
    variantOverride: VueTypes.string,
    isUppercase: VueTypes.bool.def(true),
  },
  computed: {
    normalizedTextColor() {
      if (this.loading) {
        return lsColors.transparent;
      }

      return 'current-color';
    },
  },
  methods: {
    onClick(evt) {
      lsTrackingService.track({ event: 'click', target: this.trackId });
      this.$emit('click', evt);
    },
  },
};
</script>

<style lang="scss" scoped>
.add-right-padding {
  padding-right: 10px;
}

.min-width {
  min-width: 150px;
}

.themed-component {
  &.is-primary {
    background-color: $theme-color-primary;
    color: $theme-color-white;

    &:hover {
      background-color: $theme-color-primary-hover;
    }
  }

  &.is-primary-light {
    background-color: $theme-color-primary-light;
    color: $theme-color-primary;

    &:hover {
      background-color: $theme-color-primary-light-hover;
    }
  }

  &.is-tertiary {
    background-color: $theme-color-secondary;
    color: $theme-color-secondary-button-text;

    &:hover {
      background-color: $theme-color-secondary-hover;
    }
  }

  &.is-tertiary.variant-primary-outlined {
    background-color: white;
    color: $theme-color-gray-darker;
    border-color: $theme-color-gray-darker;
    &:hover,
    &:active,
    &:focus {
      background-color: white;
      color: $theme-color-primary;
      border-color: $theme-color-primary;
    }
  }
}
</style>
